import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import {
  Archive,
  Download,
  FileText,
  DownloadCloud,
  FolderTree,
  History,
  List,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import {
  DataTable,
  type Column,
} from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import { SearchInput } from "@/components/data/search-input";
import { RefreshButton } from "@/components/data/refresh-button";
import {
  DateRangeFilter,
  dayToIso,
} from "@/components/data/date-range-filter";
import { receiptsApi, type Receipt } from "@/api/receipts";
import { executionsApi } from "@/api/executions";
import { ApiError } from "@/api/client";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { authErrorMessage } from "@/auth/errors";
import { formatDateTime } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ExportsDock } from "./-components/exports-dock";
import { DayTree } from "./-components/day-tree";

const searchSchema = z.object({
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  // Origen de la boleta. Va en la URL para que el filtro se pueda compartir.
  // `.catch(undefined)`: un ?tipo= inválido degrada a "todas".
  tipo: z.enum(["inscripcion", "baja"]).optional().catch(undefined),
  // `.catch(undefined)`: un ?view= inválido (URL editada a mano) degrada a la
  // vista normal en vez de reventar validateSearch con un ZodError.
  view: z.enum(["normal", "grouped"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/receipts/")({
  validateSearch: searchSchema,
  component: ReceiptsPage,
});

function ReceiptsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [dockOpen, setDockOpen] = useState(false);

  const listParams = {
    from: dayToIso(search.from, "start"),
    to: dayToIso(search.to, "end"),
    tipo: search.tipo,
    limit: 25,
  };
  const listQuery = useCursorList({
    queryKey: queryKeys.receipts.list(listParams),
    fetcher: receiptsApi.list,
    params: listParams,
  });

  // Búsqueda server-side (endpoint dedicado, sin paginación): busca en TODAS las
  // boletas por contrato / folio del RUG / nº de operación, no solo las cargadas.
  const q = (search.q ?? "").trim();
  const isSearching = !!q;
  const searchQuery = useQuery({
    // El tipo entra en la llave y en la consulta: el buscador ignoraba el filtro y
    // devolvía de los dos tipos aunque la barra tuviera uno elegido.
    queryKey: queryKeys.receipts.search({ q, tipo: search.tipo }),
    queryFn: () => receiptsApi.search({ q, tipo: search.tipo }),
    enabled: isSearching,
  });

  const rows = isSearching ? (searchQuery.data ?? []) : listQuery.items;

  // Vista agrupada (árbol por fecha de descarga). La búsqueda no aplica al
  // árbol, así que al buscar se fuerza la vista normal. El toggle vive en la URL.
  const grouped = !isSearching && search.view === "grouped";
  const setView = (v: "normal" | "grouped") =>
    navigate({
      search: (prev) => ({ ...prev, view: v === "normal" ? undefined : v }),
      replace: true,
    });
  // El endpoint agrupa por día LOCAL (Mexico_City); le pasamos los días
  // calendario crudos (YYYY-MM-DD del picker), NO ISO UTC. Así la ventana se
  // alinea a días locales completos y el conteo del nodo cuadra con lo que trae
  // la expansión (que usa los límites que el propio backend devolvió).
  const daysParams = {
    from: search.from,
    to: search.to,
    tipo: search.tipo,
  };
  const daysQuery = useQuery({
    queryKey: queryKeys.receipts.days(daysParams),
    queryFn: () => receiptsApi.days(daysParams),
    enabled: grouped,
  });

  // Selección múltiple para descarga masiva. Opera sobre las filas cargadas
  // (Set de ids), y persiste al paginar o alternar búsqueda.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (ids: string[]) =>
    setSelectedIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  const rowIds = rows.map((r) => r.id);
  const allChecked = rowIds.length > 0 && rowIds.every((id) => selectedIds.has(id));
  const someChecked = rowIds.some((id) => selectedIds.has(id));

  // Exportación asíncrona: crea el job y responde al instante; el ZIP lo arma un
  // worker. Abrimos el dock para que el usuario siga el avance y descargue al terminar.
  const createExportMutation = useMutation({
    mutationFn: (ids: string[]) => receiptsApi.createExport({ receiptIds: ids }),
    onSuccess: () => {
      toast.success(t("receipts.exports.started"));
      setSelectedIds(new Set());
      setDockOpen(true);
      qc.invalidateQueries({ queryKey: queryKeys.receipts.exports() });
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const runBoletasMutation = useMutation({
    mutationFn: () => executionsApi.runBoletas(),
    onSuccess: (data) => {
      toast.success(
        data.launched
          ? t("receipts.runBoletas.launched")
          : t("receipts.runBoletas.alreadyRunning"),
      );
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const downloadMutation = useMutation({
    mutationFn: (id: string) => receiptsApi.download(id),
    onSuccess: (data) => {
      const a = document.createElement("a");
      a.href = data.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = data.fileName;
      a.click();
    },
    onError: (err) => {
      // El registro existe pero el PDF aún no está en S3 → 404. Mostramos un
      // mensaje claro en vez del error genérico (o del XML crudo de S3).
      if (err instanceof ApiError && err.status === 404) {
        toast.error(t("receipts.unavailable"));
      } else {
        toast.error(authErrorMessage(err));
      }
    },
  });

  const hasFilters = !!q || !!search.from || !!search.to;

  const columns: Column<Receipt>[] = [
    {
      id: "contrato",
      header: t("receipts.columns.contract"),
      cell: (r) => (
        <span className="font-medium font-mono">{r.contractNumber ?? "—"}</span>
      ),
    },
    {
      id: "folio",
      header: t("receipts.columns.folio"),
      cell: (r) => <span className="font-mono">{r.rugFolio}</span>,
    },
    {
      id: "asiento",
      header: t("receipts.columns.registrationNumber"),
      cell: (r) => (
        <span className="font-mono text-muted-foreground">
          {r.numeroOperacion ?? "—"}
        </span>
      ),
    },
    {
      id: "tipo",
      header: t("receipts.columns.type"),
      cell: (r) => (
        <span className="text-sm">
          {r.tipo === "baja"
            ? t("receipts.type.baja")
            : t("receipts.type.inscripcion")}
        </span>
      ),
    },
    {
      id: "created",
      header: t("receipts.columns.created"),
      cell: (r) => (
        <span className="text-sm text-muted-foreground">
          {formatDateTime(r.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (r) => (
        <Button
          size="sm"
          variant="ghost"
          disabled={
            downloadMutation.isPending &&
            downloadMutation.variables === r.id
          }
          onClick={() => downloadMutation.mutate(r.id)}
        >
          <Download className="size-3.5" />
          {t("common.actions.download")}
        </Button>
      ),
      headClassName: "w-32 text-right",
      className: "text-right",
    },
  ];

  return (
    <>
      <PageHeader
        title={t("receipts.title")}
        actions={
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 ? (
              <Button
                variant="outline"
                disabled={createExportMutation.isPending}
                onClick={() => createExportMutation.mutate([...selectedIds])}
              >
                {createExportMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Archive className="size-4" />
                )}
                {t("receipts.bulk.download", { count: selectedIds.size })}
              </Button>
            ) : null}
            <Button variant="outline" onClick={() => setDockOpen(true)}>
              <History className="size-4" />
              {t("receipts.exports.open")}
            </Button>
            <RefreshButton
              onClick={() =>
                grouped ? daysQuery.refetch() : listQuery.refetch()
              }
              spinning={grouped ? daysQuery.isFetching : listQuery.isFetching}
            />
            <Button
              variant="outline"
              disabled={runBoletasMutation.isPending}
              onClick={() => runBoletasMutation.mutate()}
            >
              {runBoletasMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <DownloadCloud className="size-4" />
              )}
              {t("receipts.runBoletas.button")}
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <SearchInput
          value={search.q ?? ""}
          onChange={(v) =>
            navigate({
              search: (prev) => ({ ...prev, q: v || undefined }),
              replace: true,
            })
          }
          placeholder={t("receipts.searchPlaceholder")}
          className="sm:max-w-xl"
        />
        <DateRangeFilter
          from={search.from}
          to={search.to}
          onChange={(range) =>
            navigate({
              search: (prev) => ({ ...prev, from: range.from, to: range.to }),
              replace: true,
            })
          }
          disabled={isSearching}
        />
        {/* Origen de la boleta. Aplica también a la búsqueda. */}
        <div
          className="inline-flex rounded-md border border-border p-0.5"
          role="group"
          aria-label={t("receipts.type.label")}
        >
          {(
            [
              [undefined, t("receipts.type.all")],
              ["inscripcion", t("receipts.type.inscripcion")],
              ["baja", t("receipts.type.baja")],
            ] as const
          ).map(([value, label]) => (
            <button
              key={label}
              type="button"
              aria-pressed={search.tipo === value}
              onClick={() =>
                navigate({
                  search: (prev) => ({ ...prev, tipo: value }),
                  replace: true,
                })
              }
              className={cn(
                "rounded px-2.5 py-1 text-sm",
                search.tipo === value
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="inline-flex rounded-md border border-border p-0.5 sm:ml-auto"
          role="group"
          aria-label={t("receipts.view.label")}
        >
          <button
            type="button"
            onClick={() => setView("normal")}
            aria-pressed={!grouped}
            title={t("receipts.view.normal")}
            className={cn(
              "flex items-center gap-1.5 rounded px-2.5 py-1 text-sm",
              !grouped
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <List className="size-4" />
            <span className="hidden sm:inline">{t("receipts.view.normal")}</span>
          </button>
          <button
            type="button"
            onClick={() => setView("grouped")}
            disabled={isSearching}
            aria-pressed={grouped}
            title={t("receipts.view.grouped")}
            className={cn(
              "flex items-center gap-1.5 rounded px-2.5 py-1 text-sm disabled:opacity-40",
              grouped
                ? "bg-muted text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <FolderTree className="size-4" />
            <span className="hidden sm:inline">{t("receipts.view.grouped")}</span>
          </button>
        </div>
      </div>

      {grouped ? (
        <DayTree
          days={daysQuery.data?.days ?? []}
          columns={columns}
          selection={{
            selectedIds,
            onToggle: toggleOne,
            onToggleAll: toggleAll,
          }}
          isLoading={daysQuery.isLoading}
          isFetching={daysQuery.isFetching}
          error={daysQuery.error}
          tipo={search.tipo}
        />
      ) : (
        <DataTable<Receipt>
          data={rows}
          columns={columns}
          getRowId={(r) => r.id}
          selection={{
            selectedIds,
            onToggle: toggleOne,
            onToggleAll: toggleAll,
            allChecked,
            someChecked,
          }}
          isLoading={isSearching ? searchQuery.isLoading : listQuery.isLoading}
          isFetching={isSearching ? searchQuery.isFetching : listQuery.isFetching}
          error={isSearching ? searchQuery.error : listQuery.error}
          loadMore={
            !isSearching && listQuery.hasNextPage
              ? {
                  hasMore: true,
                  isLoading: listQuery.isFetchingNextPage,
                  onLoadMore: () => listQuery.fetchNextPage(),
                }
              : undefined
          }
          emptyState={
            <EmptyState
              icon={FileText}
              title={
                hasFilters
                  ? t("receipts.empty.filteredTitle")
                  : t("receipts.empty.noneTitle")
              }
              description={
                hasFilters
                  ? t("receipts.empty.filteredDescription")
                  : t("receipts.empty.noneDescription")
              }
            />
          }
        />
      )}

      <ExportsDock open={dockOpen} onOpenChange={setDockOpen} />
    </>
  );
}
